/**
 * Biometric (Face ID / Touch ID) helper.
 *
 * Wraps @capgo/capacitor-native-biometric. Static import is intentional — a
 * dynamic import() creates a webpack chunk whose lazy load can hang inside
 * a Capacitor WebView. The native plugin is a no-op on web because all calls
 * go through Capacitor's bridge, which we gate with isNativePlatform().
 */
import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";

const SERVER = "myrepagency.app";

function nativeReady() {
  return Capacitor.isNativePlatform() && !!NativeBiometric;
}

export async function isBiometricAvailable() {
  if (!nativeReady()) {
    return { available: false, debug: "not-native-or-plugin-missing" };
  }
  try {
    const res = await NativeBiometric.isAvailable();
    return {
      available: !!res.isAvailable,
      // biometryType: 1 = TouchID, 2 = FaceID, 3 = FingerprintAuth, 4 = FaceAuth
      type: res.biometryType,
      debug: `isAvailable=${res.isAvailable} type=${res.biometryType}`,
    };
  } catch (e) {
    return { available: false, debug: `error: ${e?.message || e}` };
  }
}

export async function hasStoredCredentials() {
  if (!nativeReady()) return false;
  try {
    const creds = await NativeBiometric.getCredentials({ server: SERVER });
    return !!(creds && creds.username && creds.password);
  } catch {
    return false;
  }
}

export async function saveCredentials(email, password) {
  if (!nativeReady()) return false;
  try {
    await NativeBiometric.setCredentials({
      username: email,
      password: password,
      server: SERVER,
    });
    return true;
  } catch {
    return false;
  }
}

export async function deleteCredentials() {
  if (!nativeReady()) return;
  try {
    await NativeBiometric.deleteCredentials({ server: SERVER });
  } catch {
    /* noop */
  }
}

/**
 * Prompt Face ID / Touch ID and, on success, return stored credentials.
 * Returns null if cancelled, failed, or unavailable.
 */
export async function authenticateAndGetCredentials() {
  if (!nativeReady()) return null;
  try {
    await NativeBiometric.verifyIdentity({
      reason: "Sign in to My Rep Agency",
      title: "Sign in",
      subtitle: "Use Face ID to access your account",
      description: "",
    });
    const creds = await NativeBiometric.getCredentials({ server: SERVER });
    if (creds && creds.username && creds.password) return creds;
    return null;
  } catch {
    return null;
  }
}

export function isNativeIOS() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}
